import NumberFlow from '@number-flow/react'
import MdiCheckCircle from '~icons/mdi/check-circle'
import { CreditsIcon } from 'src/components/Membership/MembershipIcon'
import { UIButton } from 'src/components/ui/Button'
import Text from 'src/components/ui/Text'
import DecorationRenderer from 'src/features/UserDecoration/components/UserDecoration'
import {
  CONTAINED_DECORATION_PREVIEW_CLASSES,
  useDecorationLabel,
} from 'src/features/UserDecoration/utils/decorationTypeLabel'
import { cx } from 'src/utils/cx'
import type {
  OneTimePackCardDecoration,
  OneTimePackCardRow,
} from '../domain/oneTimeCardData'
import { formatLocalizedNumber } from '../utils/formatLocalizedNumber'

export type { OneTimePackCardRow }

export interface OneTimePackCardProps {
  /** Title from marketingMetadata, pre-localized. */
  title: string
  /** Card content rows (extras + total). */
  rows: OneTimePackCardRow[]
  /** Decoration items (avatar frames, badges, …) — labelled async via `useDecoration(code)` and rendered as a preview overlay. */
  decorations: OneTimePackCardDecoration[]
  /** Multiplier badge text (e.g. "2.5"). Null = no badge. */
  multiplier?: string | null
  /** Boosted credits as number for NumberFlow. */
  boostedCredits: number
  /** Pre-formatted price string. */
  priceLabel: string
  /**
   * Active app locale (e.g. `en`, `ja`, `zh`). Used by the plain-text
   * fallback to keep number formatting in sync with the rest of the UI;
   * NumberFlow handles its own localization via its `locales` prop.
   */
  locale: string
  animateCredits: boolean
  disabled?: boolean
  showSuccess?: boolean
  onPurchase?: () => void
  /** Pre-resolved "≈ 100x images" chip text (V3 only). Null = render a placeholder. */
  equivalentImagesText?: string | null
  /** Featured / best-value treatment (V3 only). */
  featured?: boolean
  /** Show a busy spinner on the purchase button while a purchase is in flight (V3 only). */
  busy?: boolean
  /** Render a kaisuuken card icon next to the credit icon at the top of the card (V3 only). */
  hasKaisuuken?: boolean
}

export function OneTimePackCard({
  title,
  rows,
  decorations,
  multiplier,
  boostedCredits,
  priceLabel,
  locale,
  animateCredits,
  disabled,
  showSuccess,
  onPurchase,
}: OneTimePackCardProps) {
  const nonTotalRows = rows.filter(row => !row.total)
  const totalRow = rows.find(row => row.total)
  return (
    <div
      className="relative w-full min-w-28 xs:min-w-40 xs:max-w-48 md:min-w-60 md:w-60 mt-4 flex flex-col bg-[var(--credit-card-background)] dark:bg-[var(--credit-card-background-dark)] rounded-2xl ring-1 ring-transparent font-inter hover:shadow-lg transition cursor-pointer"
      onClick={onPurchase}
    >
      <div className="absolute inset-0 rounded-2xl border border-[var(--credit-card-border)] dark:border-[var(--credit-card-border-dark)] pointer-events-none" />
      <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 z-10 bg-[--one-time-badge-bg] text-white py-0.5 px-2 text-center text-xs md:text-sm font-semibold uppercase rounded-full tracking-wide whitespace-nowrap">
        <Text
          i18nKey="membership:package.one-time.badge.label"
          defaults="One-time Offer"
        />
      </div>

      <div className="flex-1 px-3 md:px-4 pt-7 md:pt-8 pb-3 flex flex-col items-center gap-1">
        {title && (
          <div className="text-xs md:text-sm font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
            {title}
          </div>
        )}

        {multiplier ? (
          <div className="text-sm md:text-base font-semibold leading-none text-[--bonus-green]">
            <Text
              i18nKey="membership:extra-pack.card.save-text"
              values={{ multiplier }}
              defaults="{{multiplier}}x Value"
            />
          </div>
        ) : (
          <div className="h-4 md:h-5" />
        )}

        <div className="flex items-center gap-1.5">
          <CreditsIcon className="text-dspurple-400 size-5 md:size-6 shrink-0" />
          <span className="text-2xl md:text-3xl font-bold leading-none text-neutral-900 dark:text-neutral-50">
            {animateCredits ? (
              <NumberFlow value={boostedCredits} />
            ) : (
              formatLocalizedNumber(boostedCredits, locale)
            )}
          </span>
        </div>

        {(nonTotalRows.length > 0 ||
          decorations.length > 0 ||
          totalRow != null) && (
          <ul className="w-full mt-1 space-y-0.5 text-xs md:text-sm">
            {nonTotalRows.map(row => (
              <PackContentRow key={`${row.label}-${row.value}`} row={row} />
            ))}
            {decorations.map(decoration => (
              <DecorationContentRow
                key={decoration.code}
                code={decoration.code}
                quantity={decoration.quantity}
              />
            ))}
            {totalRow && (
              <PackContentRow
                key={`${totalRow.label}-${totalRow.value}-total`}
                row={totalRow}
              />
            )}
          </ul>
        )}

        {decorations.length > 0 && (
          <div className="self-end -mr-1 md:-mr-2 mt-1 pointer-events-none flex flex-col items-end gap-1">
            {decorations.map(decoration => (
              <div
                key={decoration.code}
                className="relative size-10 md:size-12 overflow-hidden"
              >
                <DecorationRenderer
                  code={decoration.code}
                  className={CONTAINED_DECORATION_PREVIEW_CLASSES}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <UIButton
        className={cx(
          'h-8 md:h-12 flex-none shrink-0 items-center z-10 justify-center text-xs md:text-lg font-bold transition-all duration-200 rounded-t-none rounded-b-2xl',
          disabled
            ? 'bg-black/15 text-black/50 hover:bg-black/15 dark:bg-white/15 dark:text-white/50 dark:hover:bg-white/15'
            : showSuccess
              ? 'bg-green-700'
              : 'bg-dspurple-500 hover:bg-dspurple-500/90 dark:bg-dspurple-500 dark:hover:bg-dspurple-500',
        )}
        labelClassName="dark:text-white"
        variant="filled"
        onPress={onPurchase}
      >
        {showSuccess ? (
          <MdiCheckCircle className="size-5 inset-0 m-auto text-white animate-[scale-up_300ms_cubic-bezier(0.34,1.56,0.64,1)]" />
        ) : (
          priceLabel
        )}
      </UIButton>
    </div>
  )
}

function PackContentRow({
  row,
}: {
  row: OneTimePackCardProps['rows'][number]
}) {
  return (
    <li
      className={cx(
        'flex items-baseline justify-between gap-2',
        row.total
          ? 'font-bold text-dspurple-500 dark:text-dspurple-400'
          : 'text-neutral-500 dark:text-neutral-400',
      )}
    >
      <span className="truncate">{row.label}</span>
      <span className="font-medium shrink-0">{row.value}</span>
    </li>
  )
}

function DecorationContentRow({ code, quantity }: OneTimePackCardDecoration) {
  const label = useDecorationLabel(code)
  if (!label) return null
  return (
    <li className="flex items-baseline justify-between gap-2 text-neutral-500 dark:text-neutral-400">
      <span className="truncate">{label}</span>
      <span className="font-medium shrink-0">{`x${quantity}`}</span>
    </li>
  )
}
